/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Client Groups API
 * API for managing client groups and memberships within an organization. Client groups enable group therapy sessions, residential care groups, and other scenarios where clients participate in shared appointments.  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface AddClientToGroupRequest {
    /**
     * Client ID to add to the group
     */
    'clientId': string;
    /**
     * Membership start date (ISO 8601)
     */
    'startDate': string;
    /**
     * Membership end date (ISO 8601), null for ongoing membership
     */
    'endDate'?: string | null;
}
export interface ClientGroupDetailResponse {
    /**
     * Client group ID
     */
    'id': string;
    /**
     * Organization ID
     */
    'organizationId': string;
    /**
     * Group name
     */
    'name': string;
    /**
     * Group description
     */
    'description'?: string | null;
    /**
     * First date the group is open (ISO 8601), null when open-ended on the left. Memberships and group appointments must fall inside the window. 
     */
    'startDate'?: string | null;
    /**
     * Last date the group is open (ISO 8601), null when open-ended on the right. 
     */
    'endDate'?: string | null;
    /**
     * Number of members active on the reference date: today in Europe/Amsterdam, moved into the group\'s window. A group whose window lies entirely in the past or the future therefore reports the members it has in that window instead of 0. 
     */
    'memberCount'?: number;
    /**
     * Total number of memberships, including future-dated and ended ones. A group whose members all start next month reports `memberCount: 0` but a non-zero total, so the overview never suggests the group is empty (#3275). 
     */
    'totalMemberCount'?: number;
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
    /**
     * Last update timestamp in milliseconds
     */
    'updatedAt': number;
    /**
     * All memberships of the group — active, future-dated and ended alike. The client distinguishes them from the membership dates; filtering server-side hid every member whose period did not cover today (#3275). 
     */
    'members': Array<ClientGroupMembershipResponse>;
}
/**
 * Paginated list of client groups
 */
export interface ClientGroupListResponse {
    /**
     * List of client groups
     */
    'content': Array<ClientGroupResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of client groups
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Client membership in a group
 */
export interface ClientGroupMembershipResponse {
    /**
     * Membership ID
     */
    'id': string;
    /**
     * Client group ID
     */
    'groupId': string;
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client full name (denormalized for display)
     */
    'clientName': string;
    /**
     * Membership start date (ISO 8601)
     */
    'startDate': string;
    /**
     * Membership end date (ISO 8601), null if ongoing
     */
    'endDate'?: string | null;
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
}
/**
 * Client group basic information
 */
export interface ClientGroupResponse {
    /**
     * Client group ID
     */
    'id': string;
    /**
     * Organization ID
     */
    'organizationId': string;
    /**
     * Group name
     */
    'name': string;
    /**
     * Group description
     */
    'description'?: string | null;
    /**
     * First date the group is open (ISO 8601), null when open-ended on the left. Memberships and group appointments must fall inside the window. 
     */
    'startDate'?: string | null;
    /**
     * Last date the group is open (ISO 8601), null when open-ended on the right. 
     */
    'endDate'?: string | null;
    /**
     * Number of members active on the reference date: today in Europe/Amsterdam, moved into the group\'s window. A group whose window lies entirely in the past or the future therefore reports the members it has in that window instead of 0. 
     */
    'memberCount'?: number;
    /**
     * Total number of memberships, including future-dated and ended ones. A group whose members all start next month reports `memberCount: 0` but a non-zero total, so the overview never suggests the group is empty (#3275). 
     */
    'totalMemberCount'?: number;
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
    /**
     * Last update timestamp in milliseconds
     */
    'updatedAt': number;
}
export interface CreateClientGroupRequest {
    /**
     * Group name
     */
    'name': string;
    /**
     * Group description
     */
    'description'?: string | null;
    /**
     * First date the group is open (ISO 8601), null for open-ended
     */
    'startDate'?: string | null;
    /**
     * Last date the group is open (ISO 8601), null for open-ended
     */
    'endDate'?: string | null;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
export interface UpdateClientGroupRequest {
    /**
     * Group name
     */
    'name'?: string;
    /**
     * Group description
     */
    'description'?: string | null;
    /**
     * First date the group is open (ISO 8601), null for open-ended. Narrowing the window clamps the memberships that overlap it and is rejected (409) when a membership would fall entirely outside the new window. 
     */
    'startDate'?: string | null;
    /**
     * Last date the group is open (ISO 8601), null for open-ended
     */
    'endDate'?: string | null;
}
export interface UpdateMembershipRequest {
    /**
     * Membership start date (ISO 8601)
     */
    'startDate'?: string;
    /**
     * Membership end date (ISO 8601), null for ongoing membership
     */
    'endDate'?: string | null;
}

/**
 * ClientGroupMembershipsApi - axios parameter creator
 */
export const ClientGroupMembershipsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Adds a client to the group with specified membership period.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An omitted `endDate` inherits the group\'s end date. 
         * @summary Add client to group
         * @param {string} groupId Client group ID
         * @param {AddClientToGroupRequest} addClientToGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addClientToGroup: async (groupId: string, addClientToGroupRequest: AddClientToGroupRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('addClientToGroup', 'groupId', groupId)
            // verify required parameter 'addClientToGroupRequest' is not null or undefined
            assertParamExists('addClientToGroup', 'addClientToGroupRequest', addClientToGroupRequest)
            const localVarPath = `/api/v1/client-groups/{groupId}/members`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(addClientToGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns members of the client group. By default returns current members (active on today\'s date). Use the `date` parameter to get members active on a specific date. 
         * @summary List group members
         * @param {string} groupId Client group ID
         * @param {string} [date] Get members active on this date (ISO 8601 format YYYY-MM-DD). Defaults to today.
         * @param {boolean} [includeInactive] Include members with ended memberships
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientGroupMembers: async (groupId: string, date?: string, includeInactive?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('listClientGroupMembers', 'groupId', groupId)
            const localVarPath = `/api/v1/client-groups/{groupId}/members`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (date !== undefined) {
                localVarQueryParameter['date'] = (date as any instanceof Date) ?
                    (date as any).toISOString().substring(0,10) :
                    date;
            }

            if (includeInactive !== undefined) {
                localVarQueryParameter['includeInactive'] = includeInactive;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes a client from the group (deletes the membership record)
         * @summary Remove client from group
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeClientFromGroup: async (groupId: string, clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('removeClientFromGroup', 'groupId', groupId)
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('removeClientFromGroup', 'clientId', clientId)
            const localVarPath = `/api/v1/client-groups/{groupId}/members/{clientId}`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)))
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the membership dates for a client in the group.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An explicit null `endDate` inherits the group\'s end date. 
         * @summary Update membership
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {UpdateMembershipRequest} updateMembershipRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientGroupMembership: async (groupId: string, clientId: string, updateMembershipRequest: UpdateMembershipRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('updateClientGroupMembership', 'groupId', groupId)
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientGroupMembership', 'clientId', clientId)
            // verify required parameter 'updateMembershipRequest' is not null or undefined
            assertParamExists('updateClientGroupMembership', 'updateMembershipRequest', updateMembershipRequest)
            const localVarPath = `/api/v1/client-groups/{groupId}/members/{clientId}`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)))
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateMembershipRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientGroupMembershipsApi - functional programming interface
 */
export const ClientGroupMembershipsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientGroupMembershipsApiAxiosParamCreator(configuration)
    return {
        /**
         * Adds a client to the group with specified membership period.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An omitted `endDate` inherits the group\'s end date. 
         * @summary Add client to group
         * @param {string} groupId Client group ID
         * @param {AddClientToGroupRequest} addClientToGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addClientToGroup(groupId: string, addClientToGroupRequest: AddClientToGroupRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupMembershipResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addClientToGroup(groupId, addClientToGroupRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupMembershipsApi.addClientToGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns members of the client group. By default returns current members (active on today\'s date). Use the `date` parameter to get members active on a specific date. 
         * @summary List group members
         * @param {string} groupId Client group ID
         * @param {string} [date] Get members active on this date (ISO 8601 format YYYY-MM-DD). Defaults to today.
         * @param {boolean} [includeInactive] Include members with ended memberships
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientGroupMembers(groupId: string, date?: string, includeInactive?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ClientGroupMembershipResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientGroupMembers(groupId, date, includeInactive, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupMembershipsApi.listClientGroupMembers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes a client from the group (deletes the membership record)
         * @summary Remove client from group
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async removeClientFromGroup(groupId: string, clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.removeClientFromGroup(groupId, clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupMembershipsApi.removeClientFromGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the membership dates for a client in the group.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An explicit null `endDate` inherits the group\'s end date. 
         * @summary Update membership
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {UpdateMembershipRequest} updateMembershipRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientGroupMembership(groupId: string, clientId: string, updateMembershipRequest: UpdateMembershipRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupMembershipResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientGroupMembership(groupId, clientId, updateMembershipRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupMembershipsApi.updateClientGroupMembership']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientGroupMembershipsApi - factory interface
 */
export const ClientGroupMembershipsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientGroupMembershipsApiFp(configuration)
    return {
        /**
         * Adds a client to the group with specified membership period.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An omitted `endDate` inherits the group\'s end date. 
         * @summary Add client to group
         * @param {string} groupId Client group ID
         * @param {AddClientToGroupRequest} addClientToGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addClientToGroup(groupId: string, addClientToGroupRequest: AddClientToGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupMembershipResponse> {
            return localVarFp.addClientToGroup(groupId, addClientToGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns members of the client group. By default returns current members (active on today\'s date). Use the `date` parameter to get members active on a specific date. 
         * @summary List group members
         * @param {string} groupId Client group ID
         * @param {string} [date] Get members active on this date (ISO 8601 format YYYY-MM-DD). Defaults to today.
         * @param {boolean} [includeInactive] Include members with ended memberships
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientGroupMembers(groupId: string, date?: string, includeInactive?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<Array<ClientGroupMembershipResponse>> {
            return localVarFp.listClientGroupMembers(groupId, date, includeInactive, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes a client from the group (deletes the membership record)
         * @summary Remove client from group
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeClientFromGroup(groupId: string, clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.removeClientFromGroup(groupId, clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the membership dates for a client in the group.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An explicit null `endDate` inherits the group\'s end date. 
         * @summary Update membership
         * @param {string} groupId Client group ID
         * @param {string} clientId Client ID
         * @param {UpdateMembershipRequest} updateMembershipRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientGroupMembership(groupId: string, clientId: string, updateMembershipRequest: UpdateMembershipRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupMembershipResponse> {
            return localVarFp.updateClientGroupMembership(groupId, clientId, updateMembershipRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientGroupMembershipsApi - object-oriented interface
 */
export class ClientGroupMembershipsApi extends BaseAPI {
    /**
     * Adds a client to the group with specified membership period.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An omitted `endDate` inherits the group\'s end date. 
     * @summary Add client to group
     * @param {string} groupId Client group ID
     * @param {AddClientToGroupRequest} addClientToGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addClientToGroup(groupId: string, addClientToGroupRequest: AddClientToGroupRequest, options?: RawAxiosRequestConfig) {
        return ClientGroupMembershipsApiFp(this.configuration).addClientToGroup(groupId, addClientToGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns members of the client group. By default returns current members (active on today\'s date). Use the `date` parameter to get members active on a specific date. 
     * @summary List group members
     * @param {string} groupId Client group ID
     * @param {string} [date] Get members active on this date (ISO 8601 format YYYY-MM-DD). Defaults to today.
     * @param {boolean} [includeInactive] Include members with ended memberships
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientGroupMembers(groupId: string, date?: string, includeInactive?: boolean, options?: RawAxiosRequestConfig) {
        return ClientGroupMembershipsApiFp(this.configuration).listClientGroupMembers(groupId, date, includeInactive, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes a client from the group (deletes the membership record)
     * @summary Remove client from group
     * @param {string} groupId Client group ID
     * @param {string} clientId Client ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public removeClientFromGroup(groupId: string, clientId: string, options?: RawAxiosRequestConfig) {
        return ClientGroupMembershipsApiFp(this.configuration).removeClientFromGroup(groupId, clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the membership dates for a client in the group.  The membership period must fall inside the group\'s date window; a period outside it is rejected with 422. An explicit null `endDate` inherits the group\'s end date. 
     * @summary Update membership
     * @param {string} groupId Client group ID
     * @param {string} clientId Client ID
     * @param {UpdateMembershipRequest} updateMembershipRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientGroupMembership(groupId: string, clientId: string, updateMembershipRequest: UpdateMembershipRequest, options?: RawAxiosRequestConfig) {
        return ClientGroupMembershipsApiFp(this.configuration).updateClientGroupMembership(groupId, clientId, updateMembershipRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientGroupsApi - axios parameter creator
 */
export const ClientGroupsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new client group in the authenticated user\'s organization
         * @summary Create client group
         * @param {CreateClientGroupRequest} createClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientGroup: async (createClientGroupRequest: CreateClientGroupRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createClientGroupRequest' is not null or undefined
            assertParamExists('createClientGroup', 'createClientGroupRequest', createClientGroupRequest)
            const localVarPath = `/api/v1/client-groups`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a client group. Fails if the group has associated appointments.
         * @summary Delete client group
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientGroup: async (groupId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('deleteClientGroup', 'groupId', groupId)
            const localVarPath = `/api/v1/client-groups/{groupId}`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a client group by ID with all of its members (active, future and ended)
         * @summary Get client group by ID
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientGroupById: async (groupId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('getClientGroupById', 'groupId', groupId)
            const localVarPath = `/api/v1/client-groups/{groupId}`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of client groups in the authenticated user\'s organization
         * @summary List client groups
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;name,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: name, startDate, endDate, createdAt, updatedAt. Any other field falls back to name.
         * @param {string} [search] Search query (searches name and description)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientGroups: async (page?: number, size?: number, sort?: string, search?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/client-groups`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates a client group\'s details.  Narrowing the group\'s date window clamps every membership that still overlaps it. When a membership would fall entirely outside the new window the update is rejected (409) rather than silently dropping the client from the group. 
         * @summary Update client group
         * @param {string} groupId Client group ID
         * @param {UpdateClientGroupRequest} updateClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientGroup: async (groupId: string, updateClientGroupRequest: UpdateClientGroupRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'groupId' is not null or undefined
            assertParamExists('updateClientGroup', 'groupId', groupId)
            // verify required parameter 'updateClientGroupRequest' is not null or undefined
            assertParamExists('updateClientGroup', 'updateClientGroupRequest', updateClientGroupRequest)
            const localVarPath = `/api/v1/client-groups/{groupId}`
                .replace(`{${"groupId"}}`, encodeURIComponent(String(groupId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientGroupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientGroupsApi - functional programming interface
 */
export const ClientGroupsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientGroupsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new client group in the authenticated user\'s organization
         * @summary Create client group
         * @param {CreateClientGroupRequest} createClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClientGroup(createClientGroupRequest: CreateClientGroupRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClientGroup(createClientGroupRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupsApi.createClientGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a client group. Fails if the group has associated appointments.
         * @summary Delete client group
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientGroup(groupId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientGroup(groupId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupsApi.deleteClientGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a client group by ID with all of its members (active, future and ended)
         * @summary Get client group by ID
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientGroupById(groupId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientGroupById(groupId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupsApi.getClientGroupById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of client groups in the authenticated user\'s organization
         * @summary List client groups
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;name,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: name, startDate, endDate, createdAt, updatedAt. Any other field falls back to name.
         * @param {string} [search] Search query (searches name and description)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientGroups(page?: number, size?: number, sort?: string, search?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientGroups(page, size, sort, search, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupsApi.listClientGroups']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates a client group\'s details.  Narrowing the group\'s date window clamps every membership that still overlaps it. When a membership would fall entirely outside the new window the update is rejected (409) rather than silently dropping the client from the group. 
         * @summary Update client group
         * @param {string} groupId Client group ID
         * @param {UpdateClientGroupRequest} updateClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientGroup(groupId: string, updateClientGroupRequest: UpdateClientGroupRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientGroupResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientGroup(groupId, updateClientGroupRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientGroupsApi.updateClientGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientGroupsApi - factory interface
 */
export const ClientGroupsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientGroupsApiFp(configuration)
    return {
        /**
         * Creates a new client group in the authenticated user\'s organization
         * @summary Create client group
         * @param {CreateClientGroupRequest} createClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientGroup(createClientGroupRequest: CreateClientGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupResponse> {
            return localVarFp.createClientGroup(createClientGroupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a client group. Fails if the group has associated appointments.
         * @summary Delete client group
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientGroup(groupId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientGroup(groupId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a client group by ID with all of its members (active, future and ended)
         * @summary Get client group by ID
         * @param {string} groupId Client group ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientGroupById(groupId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupDetailResponse> {
            return localVarFp.getClientGroupById(groupId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of client groups in the authenticated user\'s organization
         * @summary List client groups
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;name,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: name, startDate, endDate, createdAt, updatedAt. Any other field falls back to name.
         * @param {string} [search] Search query (searches name and description)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientGroups(page?: number, size?: number, sort?: string, search?: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupListResponse> {
            return localVarFp.listClientGroups(page, size, sort, search, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates a client group\'s details.  Narrowing the group\'s date window clamps every membership that still overlaps it. When a membership would fall entirely outside the new window the update is rejected (409) rather than silently dropping the client from the group. 
         * @summary Update client group
         * @param {string} groupId Client group ID
         * @param {UpdateClientGroupRequest} updateClientGroupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientGroup(groupId: string, updateClientGroupRequest: UpdateClientGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientGroupResponse> {
            return localVarFp.updateClientGroup(groupId, updateClientGroupRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientGroupsApi - object-oriented interface
 */
export class ClientGroupsApi extends BaseAPI {
    /**
     * Creates a new client group in the authenticated user\'s organization
     * @summary Create client group
     * @param {CreateClientGroupRequest} createClientGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClientGroup(createClientGroupRequest: CreateClientGroupRequest, options?: RawAxiosRequestConfig) {
        return ClientGroupsApiFp(this.configuration).createClientGroup(createClientGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a client group. Fails if the group has associated appointments.
     * @summary Delete client group
     * @param {string} groupId Client group ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientGroup(groupId: string, options?: RawAxiosRequestConfig) {
        return ClientGroupsApiFp(this.configuration).deleteClientGroup(groupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a client group by ID with all of its members (active, future and ended)
     * @summary Get client group by ID
     * @param {string} groupId Client group ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientGroupById(groupId: string, options?: RawAxiosRequestConfig) {
        return ClientGroupsApiFp(this.configuration).getClientGroupById(groupId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of client groups in the authenticated user\'s organization
     * @summary List client groups
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;name,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: name, startDate, endDate, createdAt, updatedAt. Any other field falls back to name.
     * @param {string} [search] Search query (searches name and description)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientGroups(page?: number, size?: number, sort?: string, search?: string, options?: RawAxiosRequestConfig) {
        return ClientGroupsApiFp(this.configuration).listClientGroups(page, size, sort, search, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates a client group\'s details.  Narrowing the group\'s date window clamps every membership that still overlaps it. When a membership would fall entirely outside the new window the update is rejected (409) rather than silently dropping the client from the group. 
     * @summary Update client group
     * @param {string} groupId Client group ID
     * @param {UpdateClientGroupRequest} updateClientGroupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientGroup(groupId: string, updateClientGroupRequest: UpdateClientGroupRequest, options?: RawAxiosRequestConfig) {
        return ClientGroupsApiFp(this.configuration).updateClientGroup(groupId, updateClientGroupRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



